import React from 'react';
import { Users, Heart, BookOpen, Coffee, MapPin, Calendar, ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useEvent, calendarEvent } from '../i18n/EventProvider';
import AddToCalendar from './AddToCalendar';

const ICONS = [Users, Heart, BookOpen];

export default function Gathering() {
  const { t, lang } = useI18n();
  const upcomingEvents = [calendarEvent(useEvent().event, lang)];
  return (
    <section id="gathering" className="relative py-24 lg:py-32 bg-[#f5efe3]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">{t.gathering.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">
            {t.gathering.title_1}<br /><span className="italic">{t.gathering.title_2}</span>
          </h2>
          <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed">{t.gathering.sub}</p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {t.gathering.cards.map((p, i) => {
            const Icon = ICONS[i];
            return (
              <div key={p.title} className="card-rise bg-white border border-[#e5dcc5] rounded-2xl p-7">
                <div className="w-12 h-12 rounded-xl bg-[#f0e9d8] flex items-center justify-center text-[#2e5340] mb-5">
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif-display text-2xl text-[#1f3a2a]">{p.title}</h3>
                <p className="mt-3 text-[#4a5a4e] text-sm leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-5">
          <div className="card-rise bg-[#1f3a2a] text-[#f5efe3] rounded-2xl p-7 flex items-start gap-4">
            <span className="w-11 h-11 rounded-xl bg-[#2e5340] flex items-center justify-center text-[#d4a63d] shrink-0"><Coffee size={20} /></span>
            <div>
              <h4 className="font-serif-display text-xl">{t.gathering.coffeeTitle}</h4>
              <p className="mt-2 text-[#f5efe3]/75 text-sm leading-relaxed">{t.gathering.coffeeText}</p>
            </div>
          </div>
          <div className="card-rise bg-[#f0e9d8] text-[#1f3a2a] rounded-2xl p-7 flex items-start gap-4">
            <span className="w-11 h-11 rounded-xl bg-[#e5d9b4] flex items-center justify-center text-[#1f3a2a] shrink-0"><MapPin size={20} /></span>
            <div>
              <h4 className="font-serif-display text-xl">{t.gathering.whenTitle}</h4>
              <p className="mt-2 text-[#4a5a4e] text-sm leading-relaxed">{t.gathering.whenText}</p>
            </div>
          </div>
        </div>

        <div className="mt-14">
          <div className="flex items-center gap-2 mb-5">
            <Calendar size={16} className="text-[#b88a2a]" />
            <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">
              {lang === 'es' ? 'Próximas reuniones' : 'Upcoming gatherings'}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {upcomingEvents.map((ev) => (
              <div
                key={ev.id}
                className="card-rise group relative overflow-hidden bg-white border border-[#e5dcc5] rounded-2xl p-7 flex flex-col"
                data-testid="upcoming-event-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-block text-[10px] uppercase tracking-wider-xtra px-2.5 py-1 rounded-full bg-[#1f3a2a] text-[#d4a63d] font-medium">
                      {ev.label}
                    </span>
                    <h4 className="mt-3 font-serif-display text-2xl text-[#1f3a2a] leading-tight">
                      {ev.dateLong}
                    </h4>
                    <p className="mt-1 text-[#8a7a4e] text-sm">{ev.time}</p>
                  </div>
                  <a href={ev.mapsUrl} target="_blank" rel="noreferrer" aria-label="Open in Google Maps" className="text-[#8a7a4e] hover:text-[#1f3a2a] hover:-translate-y-0.5 hover:translate-x-0.5 transition-all">
                    <ArrowUpRight size={20} />
                  </a>
                </div>
                <div className="mt-5 pt-5 border-t border-[#e5dcc5] flex items-start gap-3">
                  <MapPin size={16} className="text-[#2e5340] shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="text-[#1f3a2a] font-medium">{ev.venue}</div>
                    <div className="text-[#4a5a4e] mt-0.5">{ev.address}</div>
                  </div>
                </div>
                <div className="mt-5">
                  <AddToCalendar event={ev} variant="light" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
